import {
  type ActionCard,
  ChatStatus,
  config,
  isAgentThread,
  isChatStatusBusy,
  useStores,
} from '@littlebird/sdk';
import { findLinkedActionCardIds } from '@littlebird/ui-utils';
import { getRouterContext } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import { AnimatePresence, motion } from 'motion/react';
import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { Virtualizer } from 'virtua';

import type { ChatItem } from '@/components/blocks/Message/MessageList/chatItems.types';
import { ChatScrollToLastMessageButton } from '@/components/blocks/Message/MessageList/ChatScrollToLastMessageButton';
import { buildChatItems } from '@/components/blocks/Message/MessageList/MessageList';
import { CHAT_THREAD_ID } from '@/components/blocks/Message/MessageList/useChatScroll';
import {
  ReasoningStatus,
  type ReasoningStep,
} from '@/components/blocks/Message/reasoning';
import { BrowserRequestHost } from '@/features/browser-stream/browser-request-host';
import { hivemindGroupAnswerIds } from '@/features/hivemind';
import { afterFrames } from '@/lib/animate';
import { logError } from '@/lib/errorLogging';
import { uiStore } from '@/stores/ui.store';
import { classList } from '@/utils/cn';

import { Marker, MarkerContent } from './agents-marker';
import AgentMessage from './agents-message';
import { AgentWorkingIndicator } from './agents-working-indicator';
import { agentsStore } from './agents.store';
import {
  deriveWorkingVerb,
  groupCardsByMessage,
  isAgentPrimingMessage,
  isAgentProfileUpdateMessage,
  isTurnUsingTool,
} from './agents.utils';

const TOP_LOAD_THRESHOLD_PX = 400;
const WORKING_INDICATOR_LINGER_MS = 600;
const AT_BOTTOM_EPSILON_PX = 24;
const SCROLL_TO_END_BUTTON_THRESHOLD_PX = 8;
const PREPEND_SETTLE_FRAMES = 2;
const ESTIMATED_ROW_PX = 86;
const CONTENT_TOP_PADDING_PX = 48;
const UPWARD_SCROLL_KEYS = new Set(['ArrowUp', 'PageUp', 'Home']);

type AgentMessageListProps = {
  threadId: string;
  height: number;
  onAskUserComplete: () => void;
};

export const AgentMessageList = observer(
  ({ threadId, height, onAskUserComplete }: AgentMessageListProps) => {
    // Cards arrive by sync under the same MobX store, so React Compiler must not cache these reads
    'use no memo';
    const { threads, messages: messagesStore, actionCards } = useStores();
    const router = useContext(getRouterContext());
    const isSharedChat = Boolean(
      router?.matchRoute({ to: '/shared-chat/$id' })
    );
    const thread = threads.getById(threadId);
    const viewportRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLDivElement>(null);
    const atBottomRef = useRef(true);
    const lastScrollTopRef = useRef(0);
    const [canScrollToEnd, setCanScrollToEnd] = useState(false);
    const loadingOlderRef = useRef(false);
    const [isShifting, setIsShifting] = useState(false);
    const chatWidth = uiStore.chatWidth;
    const scrollToEnd = useCallback((behavior: ScrollBehavior = 'auto') => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      viewport.scrollTo({
        top: viewport.scrollHeight - viewport.clientHeight,
        behavior,
      });
    }, []);
    const syncScrollToEndButton = useCallback(() => {
      const viewport = viewportRef.current;
      if (!viewport) return;
      const gap =
        viewport.scrollHeight - viewport.scrollTop - viewport.clientHeight;
      setCanScrollToEnd(
        !atBottomRef.current && gap > SCROLL_TO_END_BUTTON_THRESHOLD_PX
      );
    }, []);

    const hasThread = thread !== undefined;
    useEffect(() => {
      const viewport = viewportRef.current;
      const content = contentRef.current;
      if (!viewport || !content) return;
      const observer = new ResizeObserver(() => {
        if (atBottomRef.current) scrollToEnd();
        syncScrollToEndButton();
      });
      observer.observe(viewport);
      observer.observe(content);
      return () => observer.disconnect();
    }, [hasThread, scrollToEnd, syncScrollToEndButton]);

    const isBusy = isChatStatusBusy(thread?.status);

    const allMessages = messagesStore.getMessagesByThreadId(threadId);
    const answersInTables = isSharedChat
      ? new Set<string>()
      : hivemindGroupAnswerIds(allMessages, actionCards.getByChatId(threadId));
    const visibleMessages = allMessages.filter(
      (message) => !isAgentPrimingMessage(message)
    );

    const agent =
      thread && isAgentThread(thread)
        ? agentsStore.getById(thread.agentId)
        : undefined;
    const introPending =
      agent !== undefined && !agent.description && visibleMessages.length === 0;
    const isWorking =
      isBusy ||
      introPending ||
      (thread?.status === ChatStatus.NotSubmitted &&
        visibleMessages[visibleMessages.length - 1]?.role === 'user');

    const builtItems = buildChatItems(visibleMessages, thread, {
      showThinking: true,
      showThoughts: config.chat.show_thoughts,
      answersInTables,
    });
    const activeWorkingStep = findActiveWorkingStep(builtItems);
    const chatItems = builtItems.filter(
      (item) =>
        item.type === 'userMessage' ||
        item.type === 'assistantResponse' ||
        item.type === 'dateSeparator'
    );
    const renderedReplies = chatItems.flatMap((item) =>
      item.type === 'assistantResponse' ? [item.message] : []
    );
    const renderedReplyIds = new Set(renderedReplies.map((reply) => reply.id));
    const linkedHosts = new Map<number, string>();
    for (const reply of renderedReplies) {
      for (const localId of findLinkedActionCardIds(reply.displayContent)) {
        if (!linkedHosts.has(localId)) linkedHosts.set(localId, reply.id);
      }
    }
    const cardsByMessageId = groupCardsByMessage(
      visibleMessages,
      renderedReplyIds,
      actionCards.getByChatId(threadId),
      {
        replyPending: isBusy,
        olderMessagesUnloaded:
          messagesStore.hasOlderMessages(threadId) ||
          messagesStore.isThreadHistorySyncing(threadId),
        linkedHosts,
      }
    );
    if (
      (chatItems.length > 0 || isWorking) &&
      chatItems[0]?.type !== 'dateSeparator'
    ) {
      chatItems.unshift({
        type: 'dateSeparator',
        date: 'Today',
        id: 'date-today-lead',
      });
    }

    const [showWorkingFooter, setShowWorkingFooter] = useState(isWorking);
    useEffect(() => {
      if (isWorking) {
        setShowWorkingFooter(true);
        return;
      }
      const timer = setTimeout(
        () => setShowWorkingFooter(false),
        WORKING_INDICATOR_LINGER_MS
      );
      return () => clearTimeout(timer);
    }, [isWorking]);
    const workingVerb = deriveWorkingVerb(activeWorkingStep);
    const lastLiveVerbRef = useRef(workingVerb);
    if (isWorking) lastLiveVerbRef.current = workingVerb;
    const displayedVerb = isWorking ? workingVerb : lastLiveVerbRef.current;
    const usingTool = isTurnUsingTool(visibleMessages, thread?.status);
    const lastLiveUsingToolRef = useRef(usingTool);
    if (isWorking) lastLiveUsingToolRef.current = usingTool;
    const displayedUsingTool = isWorking
      ? usingTool
      : lastLiveUsingToolRef.current;

    const [isRevealed, setIsRevealed] = useState(false);
    const [initialLoadDone, setInitialLoadDone] = useState(false);
    const revealScheduledRef = useRef(false);
    const isMountedRef = useRef(true);
    const initialMessageIdsRef = useRef<Set<string> | null>(null);
    if (initialLoadDone && initialMessageIdsRef.current === null) {
      initialMessageIdsRef.current = new Set(
        visibleMessages.map((message) => message.id)
      );
    }
    const initialMessageIds = initialMessageIdsRef.current;
    useEffect(() => {
      isMountedRef.current = true;
      return () => {
        isMountedRef.current = false;
      };
    }, []);

    useEffect(() => {
      let cancelled = false;
      messagesStore
        .loadThreadMessages(threadId)
        .catch((error: unknown) => {
          logError(error, 'Failed to load the agent chat', {
            context: 'AgentMessageList',
            metadata: { threadId },
          });
        })
        .finally(() => {
          if (!cancelled) setInitialLoadDone(true);
        });
      return () => {
        cancelled = true;
      };
    }, [threadId, messagesStore]);

    useLayoutEffect(() => {
      if (isRevealed || revealScheduledRef.current) return;
      const reveal = () => {
        if (!isMountedRef.current) return;
        setIsRevealed(true);
      };
      if (chatItems.length === 0) {
        if (initialLoadDone) reveal();
        return;
      }
      revealScheduledRef.current = true;
      afterFrames(3, () => {
        scrollToEnd();
        afterFrames(1, reveal);
      });
      setTimeout(reveal, 350);
    }, [isRevealed, chatItems.length, initialLoadDone, scrollToEnd, threadId]);

    const loadOlder = useCallback(async () => {
      if (loadingOlderRef.current || !threadId) return;
      if (!messagesStore.hasOlderMessages(threadId)) return;
      loadingOlderRef.current = true;
      setIsShifting(true);
      try {
        await messagesStore.loadOlderMessages(threadId);
      } finally {
        afterFrames(PREPEND_SETTLE_FRAMES, () => {
          if (!isMountedRef.current) return;
          setIsShifting(false);
          loadingOlderRef.current = false;
        });
      }
    }, [messagesStore, threadId]);

    useLayoutEffect(() => {
      if (!atBottomRef.current) return;
      scrollToEnd();
    });

    const lastUserMessageId = visibleMessages
      .filter((message) => message.role === 'user')
      .at(-1)?.id;
    const lastUserMessageIdRef = useRef(lastUserMessageId);
    useLayoutEffect(() => {
      if (lastUserMessageIdRef.current === lastUserMessageId) return;
      lastUserMessageIdRef.current = lastUserMessageId;
      if (isRevealed) scrollToEnd('smooth');
    }, [lastUserMessageId, isRevealed, scrollToEnd]);

    if (!thread) return null;

    return (
      <div
        className={classList(
          'relative w-full',
          !isRevealed && 'pointer-events-none opacity-0'
        )}
        style={{ height: `${height}px` }}
      >
        <div
          className="relative flex size-full min-h-0 flex-col overflow-hidden"
          style={{ height: `${height}px` }}
        >
          <div
            id={`${CHAT_THREAD_ID}-${threadId}`}
            ref={viewportRef}
            role="region"
            aria-label="Messages"
            tabIndex={0}
            className="agents-message-scroller scrollbar-hover no-record size-full min-h-0 min-w-0 overflow-y-auto overscroll-contain [contain:content] [overflow-anchor:none] [scrollbar-gutter:stable]"
            onWheel={(event) => {
              if (event.deltaY < 0) atBottomRef.current = false;
            }}
            onTouchMove={() => {
              atBottomRef.current = false;
            }}
            onKeyDown={(event) => {
              if (UPWARD_SCROLL_KEYS.has(event.key))
                atBottomRef.current = false;
            }}
            onScroll={(event) => {
              const viewport = event.currentTarget;
              const previousTop = lastScrollTopRef.current;
              lastScrollTopRef.current = viewport.scrollTop;
              const gap =
                viewport.scrollHeight -
                viewport.scrollTop -
                viewport.clientHeight;
              if (gap <= AT_BOTTOM_EPSILON_PX) {
                atBottomRef.current = true;
              } else if (viewport.scrollTop < previousTop) {
                atBottomRef.current = false;
              } else if (atBottomRef.current) {
                scrollToEnd();
              }
              syncScrollToEndButton();
              if (viewport.scrollTop <= TOP_LOAD_THRESHOLD_PX) {
                void loadOlder();
              }
            }}
          >
            <div
              ref={contentRef}
              role="log"
              aria-relevant="additions"
              className="flex h-max min-h-full flex-col"
              style={{ paddingTop: CONTENT_TOP_PADDING_PX }}
            >
              <Virtualizer
                scrollRef={viewportRef}
                shift={isShifting}
                overscan={8}
                itemSize={ESTIMATED_ROW_PX}
                startMargin={CONTENT_TOP_PADDING_PX}
              >
                {chatItems.map((item, index) => (
                  <div key={chatItemKey(item)}>
                    {renderAgentChatItem(
                      item,
                      index,
                      chatItems[index + 1]?.type === 'assistantResponse',
                      threadId,
                      chatWidth,
                      initialMessageIds,
                      cardsByMessageId,
                      onAskUserComplete
                    )}
                  </div>
                ))}
              </Virtualizer>
              <BrowserRequestHost threadId={threadId} className="md:px-6" />
              <div className="relative h-14 shrink-0">
                <AnimatePresence initial={false}>
                  {showWorkingFooter && (
                    <motion.div
                      key="working-footer"
                      exit={{ opacity: 0 }}
                      transition={{ duration: 0.25, ease: 'easeOut' }}
                    >
                      <RowWrapper chatWidth={chatWidth} id="agent-working-row">
                        <AgentWorkingIndicator
                          id={`thinking-${threadId}`}
                          threadId={threadId}
                          verb={displayedVerb}
                          usingTool={displayedUsingTool}
                        />
                      </RowWrapper>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </div>
        </div>
        <ChatScrollToLastMessageButton
          show={canScrollToEnd}
          isResponding={isBusy}
          onScrollToBottom={() => scrollToEnd('smooth')}
        />
      </div>
    );
  }
);

const RowWrapper = ({
  chatWidth,
  id,
  children,
}: {
  chatWidth: string;
  id: string;
  children: React.ReactNode;
}) => (
  <div
    id={id}
    className={classList(
      'mx-auto w-full px-3 md:px-6 transform-gpu',
      chatWidth
    )}
  >
    {children}
  </div>
);

const chatItemKey = (item: ChatItem) =>
  item.type === 'userMessage' || item.type === 'assistantResponse'
    ? item.message.id
    : item.id;

const renderAgentChatItem = (
  item: ChatItem,
  index: number,
  precedesAssistantMessage: boolean,
  threadId: string,
  chatWidth: string,
  initialMessageIds: Set<string> | null,
  cardsByMessageId: Map<string, ActionCard[]>,
  onAskUserComplete: () => void
) => {
  if (item.type === 'dateSeparator') {
    return (
      <Marker className="h-20 select-none justify-center px-6">
        <MarkerContent className="text-[0.8125rem] tracking-wider">
          {item.date}
        </MarkerContent>
      </Marker>
    );
  }
  if (
    item.type === 'userMessage' &&
    isAgentProfileUpdateMessage(item.message)
  ) {
    return (
      <Marker className="h-14 select-none justify-center px-6">
        <MarkerContent className="text-[0.8125rem] tracking-wider">
          You updated this agent&apos;s profile
        </MarkerContent>
      </Marker>
    );
  }
  if (item.type === 'userMessage' || item.type === 'assistantResponse') {
    return (
      <RowWrapper chatWidth={chatWidth} id={`agent-chat-item-${index}`}>
        <AgentMessage
          threadId={threadId}
          message={item.message}
          messageIndex={item.index}
          cards={cardsByMessageId.get(item.message.id)}
          isLastAssistantMessage={
            item.type === 'assistantResponse' && item.isLastInResponseSequence
          }
          precedesAssistantMessage={precedesAssistantMessage}
          animateOnAppear={
            initialMessageIds !== null &&
            !initialMessageIds.has(item.message.id)
          }
          onAskUserComplete={onAskUserComplete}
        />
      </RowWrapper>
    );
  }
  return null;
};

function findActiveWorkingStep(items: ChatItem[]): ReasoningStep | null {
  for (let i = items.length - 1; i >= 0; i--) {
    const item = items[i];
    if (item.type === 'userMessage' || item.type === 'assistantResponse') {
      return null;
    }
    if (item.type === 'reasoning') {
      if (item.status === ReasoningStatus.Complete) return null;
      return item.steps[item.steps.length - 1] ?? null;
    }
  }
  return null;
}
